/**
 * AuraGardyn - Scroll Animations & Interactive Components
 * Intersection Observer based reveal animations, FAQ accordion, testimonial slider
 */

(function () {
  'use strict';

  /* ===== Scroll Reveal Animations ===== */
  function initScrollReveal() {
    const reveals = document.querySelectorAll('.ag-reveal, .ag-reveal--left, .ag-reveal--right, .ag-reveal--scale');
    if (!reveals.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' },
    );

    reveals.forEach((el) => observer.observe(el));
  }

  /* ===== FAQ Accordion ===== */
  function initAccordion() {
    const triggers = document.querySelectorAll('.ag-accordion__trigger');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', function () {
        const item = this.closest('.ag-accordion__item');
        const content = item.querySelector('.ag-accordion__content');
        const isOpen = item.classList.contains('is-open');

        // Close all others in same accordion
        const accordion = item.closest('.ag-accordion');
        if (accordion) {
          accordion.querySelectorAll('.ag-accordion__item.is-open').forEach((openItem) => {
            if (openItem !== item) {
              openItem.classList.remove('is-open');
              openItem.querySelector('.ag-accordion__content').style.maxHeight = '0';
            }
          });
        }

        // Toggle current
        if (isOpen) {
          item.classList.remove('is-open');
          content.style.maxHeight = '0';
        } else {
          item.classList.add('is-open');
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    });
  }

  /* ===== Testimonial Slider ===== */
  function initSlider() {
    document.querySelectorAll('.ag-slider').forEach((slider) => {
      const track = slider.querySelector('.ag-slider__track');
      const slides = slider.querySelectorAll('.ag-slider__slide');
      const dots = slider.querySelectorAll('.ag-slider__dot');
      if (!track || slides.length < 2) return;

      let current = 0;
      let autoplayInterval;

      function goTo(index) {
        current = index;
        track.style.transform = `translateX(-${current * 100}%)`;
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === current));
      }

      function next() {
        goTo((current + 1) % slides.length);
      }

      // Dot click
      dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));

      // Autoplay
      function startAutoplay() {
        autoplayInterval = setInterval(next, 5000);
      }
      function stopAutoplay() {
        clearInterval(autoplayInterval);
      }

      slider.addEventListener('mouseenter', stopAutoplay);
      slider.addEventListener('mouseleave', startAutoplay);

      // Touch swipe support
      let touchStartX = 0;
      let touchEndX = 0;

      track.addEventListener(
        'touchstart',
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
          stopAutoplay();
        },
        { passive: true },
      );

      track.addEventListener(
        'touchend',
        (e) => {
          touchEndX = e.changedTouches[0].screenX;
          const diff = touchStartX - touchEndX;
          if (Math.abs(diff) > 50) {
            if (diff > 0) {
              goTo(Math.min(current + 1, slides.length - 1));
            } else {
              goTo(Math.max(current - 1, 0));
            }
          }
          startAutoplay();
        },
        { passive: true },
      );

      // Init
      if (dots.length) dots[0].classList.add('is-active');
      startAutoplay();
    });
  }

  /* ===== Floating Header Scroll Effect ===== */
  function initHeader() {
    const header = document.querySelector('.ag-header');
    if (!header) return;

    let lastScroll = 0;

    window.addEventListener(
      'scroll',
      () => {
        const scrollY = window.scrollY;
        header.classList.toggle('ag-header--scrolled', scrollY > 50);
        header.classList.toggle('ag-header--hidden', scrollY > lastScroll && scrollY > 200);
        lastScroll = scrollY;
      },
      { passive: true },
    );
  }

  /* ===== Mobile Menu ===== */
  function initMobileMenu() {
    const toggle = document.querySelector('.ag-header__menu-toggle');
    const drawer = document.querySelector('.ag-mobile-drawer');
    const overlay = document.querySelector('.ag-mobile-overlay');
    const close = document.querySelector('.ag-mobile-drawer__close');

    if (!toggle || !drawer) return;

    function openMenu() {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }
    function closeMenu() {
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', openMenu);
    if (close) close.addEventListener('click', closeMenu);
    if (overlay) overlay.addEventListener('click', closeMenu);
  }

  /* ===== Counter Animation ===== */
  function initCounters() {
    const counters = document.querySelectorAll('[data-count-to]');
    if (!counters.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const target = parseInt(el.dataset.countTo, 10);
            const duration = 2000;
            const start = 0;
            const startTime = performance.now();

            function update(currentTime) {
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              el.textContent = Math.floor(start + (target - start) * eased).toLocaleString('tr-TR');
              if (progress < 1) requestAnimationFrame(update);
            }

            requestAnimationFrame(update);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.5 },
    );

    counters.forEach((c) => observer.observe(c));
  }

  /* ===== KVKK / Cookie Consent ===== */
  function initKvkkConsent() {
    const banner = document.getElementById('ag-kvkk');
    if (!banner) return;

    let stored = null;
    try {
      stored = localStorage.getItem('kvkkConsent');
    } catch (_) {}
    if (stored !== 'accepted' && stored !== 'declined') {
      banner.removeAttribute('hidden');
    }

    banner.querySelectorAll('[data-kvkk-choice]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const choice = btn.dataset.kvkkChoice;
        try {
          localStorage.setItem('kvkkConsent', choice);
        } catch (_) {}
        banner.setAttribute('hidden', '');
        document.dispatchEvent(new CustomEvent('kvkk:choice', { detail: { choice } }));
      });
    });
  }

  /* ===== Modals / Pop-ups =====
     Markup contract:
       <div class="ag-modal" id="foo" role="dialog" aria-modal="true">
     Opening:  any element with [data-ag-modal-open="foo"]
     Closing:  [data-ag-modal-close], the overlay, or ESC
     Auto-triggers are declared on the modal itself:
       data-ag-trigger="delay|scroll|exit"   data-ag-trigger-value="15"
       data-ag-frequency="once|session|always"
     Auto-triggers wait for the KVKK consent choice so the pop-up and the
     consent banner never fight for the screen. Click triggers always work. */
  function initModals() {
    const modals = document.querySelectorAll('.ag-modal');
    if (!modals.length) return;

    const FOCUSABLE =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let lastFocused = null;

    function storageFor(modal) {
      const freq = modal.dataset.agFrequency || 'once';
      if (freq === 'always') return null;
      try {
        return freq === 'session' ? window.sessionStorage : window.localStorage;
      } catch (_) {
        return null;
      }
    }

    function alreadySeen(modal) {
      const store = storageFor(modal);
      if (!store) return false;
      try {
        return store.getItem('agModal:' + modal.id) === 'seen';
      } catch (_) {
        return false;
      }
    }

    function markSeen(modal) {
      const store = storageFor(modal);
      if (!store) return;
      try {
        store.setItem('agModal:' + modal.id, 'seen');
      } catch (_) {}
    }

    function openModal(modal) {
      if (!modal || modal.classList.contains('is-open')) return;
      // Never stack modals — close whatever is open first.
      document.querySelectorAll('.ag-modal.is-open').forEach(closeModal);

      lastFocused = document.activeElement;
      modal.classList.add('is-open');
      document.body.classList.add('ag-modal-open');

      // Lazy-load deferred iframes (video) only once the modal is opened.
      modal.querySelectorAll('iframe[data-src]').forEach((frame) => {
        if (!frame.getAttribute('src')) frame.setAttribute('src', frame.dataset.src);
      });

      const target = modal.querySelector(FOCUSABLE) || modal.querySelector('.ag-modal__panel');
      if (target) target.focus();

      markSeen(modal);
      document.dispatchEvent(new CustomEvent('ag:modal:open', { detail: { id: modal.id } }));
    }

    function closeModal(modal) {
      if (!modal || !modal.classList.contains('is-open')) return;
      modal.classList.remove('is-open');
      document.body.classList.remove('ag-modal-open');

      // Stop video playback on close.
      modal.querySelectorAll('iframe[data-src]').forEach((frame) => frame.removeAttribute('src'));
      modal.querySelectorAll('video').forEach((vid) => vid.pause());

      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      document.dispatchEvent(new CustomEvent('ag:modal:close', { detail: { id: modal.id } }));
    }

    // Open triggers (delegated, so markup injected later still works)
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-ag-modal-open]');
      if (opener) {
        e.preventDefault();
        openModal(document.getElementById(opener.dataset.agModalOpen));
        return;
      }
      const closer = e.target.closest('[data-ag-modal-close], .ag-modal__overlay');
      if (closer) {
        e.preventDefault();
        closeModal(closer.closest('.ag-modal'));
      }
    });

    // ESC to close + focus trap
    document.addEventListener('keydown', (e) => {
      const open = document.querySelector('.ag-modal.is-open');
      if (!open) return;

      if (e.key === 'Escape') {
        closeModal(open);
        return;
      }

      if (e.key !== 'Tab') return;
      const items = Array.from(open.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    /* --- Auto-triggers --- */
    function armAutoTriggers() {
      modals.forEach((modal) => {
        const trigger = modal.dataset.agTrigger;
        if (!trigger || trigger === 'manual') return;
        if (alreadySeen(modal)) return;

        const value = parseInt(modal.dataset.agTriggerValue, 10);

        if (trigger === 'delay') {
          window.setTimeout(
            () => {
              if (!alreadySeen(modal) || modal.dataset.agFrequency === 'always') openModal(modal);
            },
            (isNaN(value) ? 15 : value) * 1000,
          );
        }

        if (trigger === 'scroll') {
          const pct = isNaN(value) ? 40 : value;
          const onScroll = () => {
            const doc = document.documentElement;
            const scrolled = (window.scrollY / (doc.scrollHeight - window.innerHeight)) * 100;
            if (scrolled >= pct) {
              window.removeEventListener('scroll', onScroll);
              openModal(modal);
            }
          };
          window.addEventListener('scroll', onScroll, { passive: true });
        }

        if (trigger === 'exit') {
          // Desktop only — mobile has no meaningful exit intent.
          if (window.innerWidth < 750) return;
          const onLeave = (e) => {
            if (e.clientY > 0) return;
            document.removeEventListener('mouseout', onLeave);
            openModal(modal);
          };
          document.addEventListener('mouseout', onLeave);
        }
      });
    }

    // Wait for the KVKK choice before firing anything automatic.
    let consent = null;
    try {
      consent = localStorage.getItem('kvkkConsent');
    } catch (_) {}

    if (consent === 'accepted' || consent === 'declined') {
      armAutoTriggers();
    } else {
      document.addEventListener('kvkk:choice', armAutoTriggers, { once: true });
    }
  }

  /* ===== Init All ===== */
  function init() {
    initScrollReveal();
    initAccordion();
    initSlider();
    initHeader();
    initMobileMenu();
    initCounters();
    initKvkkConsent();
    initModals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
